import { useCallback, useMemo } from 'react'
import type { CanvasStateOutput } from '../../types/infrastructure-map'

export interface CollapseTree {
  childrenByParent: Map<string, string[]>
  findCollapsedAncestor: (endpointId: string) => string | null
  getAllDescendants: (endpointId: string) => string[]
}

interface FindAncestorContext {
  endpointId: string
  visited: Set<string>
  childrenByParent: Map<string, string[]>
  canvasStatesMap: Map<string, CanvasStateOutput>
}

function hasOwnChildren(id: string, childrenByParent: Map<string, string[]>): boolean {
  return (childrenByParent.get(id)?.length ?? 0) > 0
}

function findAncestorInner(context: FindAncestorContext): string | null {
  const { endpointId, visited, childrenByParent, canvasStatesMap } = context
  if (visited.has(endpointId)) return null
  visited.add(endpointId)

  const ownChildren = hasOwnChildren(endpointId, childrenByParent)
  for (const [parentId, children] of childrenByParent.entries()) {
    if (!children.includes(endpointId)) continue
    const result = checkParentCollapse({
      parentId,
      ownChildren,
      visited,
      childrenByParent,
      canvasStatesMap,
    })
    if (result !== undefined) return result
  }
  return null
}

interface ParentCheckContext {
  parentId: string
  ownChildren: boolean
  visited: Set<string>
  childrenByParent: Map<string, string[]>
  canvasStatesMap: Map<string, CanvasStateOutput>
}

function checkParentCollapse(context: ParentCheckContext): string | null | undefined {
  const { parentId, ownChildren, visited, childrenByParent, canvasStatesMap } = context
  const parentState = canvasStatesMap.get(parentId)
  if (parentState?.isCollapsed) {
    if (ownChildren) {
      return findAncestorInner({
        endpointId: parentId,
        visited,
        childrenByParent,
        canvasStatesMap,
      })
    }
    return parentId
  }
  const ancestorCollapsed = findAncestorInner({
    endpointId: parentId,
    visited,
    childrenByParent,
    canvasStatesMap,
  })
  if (ancestorCollapsed) return ancestorCollapsed
  return undefined
}

function collectDescendants(
  endpointId: string,
  visited: Set<string>,
  childrenByParent: Map<string, string[]>
): string[] {
  if (visited.has(endpointId)) return []
  visited.add(endpointId)
  const directChildren = childrenByParent.get(endpointId) ?? []
  const all: string[] = [...directChildren]
  for (const childId of directChildren) {
    all.push(...collectDescendants(childId, visited, childrenByParent))
  }
  return all
}

export function useCollapseTree(
  parentChildMap: Map<string, string[]> | undefined,
  canvasStatesMap: Map<string, CanvasStateOutput>
): CollapseTree {
  const childrenByParent = useMemo(
    () => parentChildMap ?? new Map<string, string[]>(),
    [parentChildMap]
  )

  const findCollapsedAncestor = useCallback(
    (endpointId: string): string | null =>
      findAncestorInner({
        endpointId,
        visited: new Set<string>(),
        childrenByParent,
        canvasStatesMap,
      }),
    [childrenByParent, canvasStatesMap]
  )

  const getAllDescendants = useCallback(
    (endpointId: string): string[] =>
      collectDescendants(endpointId, new Set<string>(), childrenByParent),
    [childrenByParent]
  )

  return { childrenByParent, findCollapsedAncestor, getAllDescendants }
}
